import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Button, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    InputAdornment, MenuItem, Skeleton, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import { useSnackbar } from "notistack";
import PageTitle from "../PageTitle";
import { apiConstants, getGeneric, patchGeneric, postGeneric, putGeneric } from "../../common/constants";
import EscalationV2Nav from "./EscalationV2Nav";
import EmsPageHeader from "./EmsPageHeader";
import { MUTED, NAVY, gradientBtnSx } from "./SectionCard";
import { invalidateCached } from "./escalationApiCache";
import { SEVERITY_META } from "./severity";
import { ACCENTS, CARD_SX, COLORS, reducedMotion, rise } from "./emsTheme";
import {
    ConfigEmpty, ConfigStat, Eyebrow, IconTile, OFF_GREY, Pill, Segmented, StatePill, cancelBtnSx, purpleControlsSx, textBtnSx, topBarCardSx,
} from "./ConfigUi";

const ADMIN = `${apiConstants.ESCALATION_DETAIL_V2}/admin/categories`;
const toHours = (m) => (m != null ? Math.round((m / 60) * 10) / 10 : "");
const toMinutes = (h) => (h === "" || h == null ? null : Math.round(Number(h) * 60));
const errMsg = (e, fallback) => e?.response?.data?.message || fallback;

// What the list shows when a subcategory has no target of its own (same as before: 4 h / 48 h).
const DEFAULT_FIRST_RESPONSE_MIN = 240;
const DEFAULT_RESOLUTION_MIN = 2880;

// Ready-made rows for the fields the ticket form already understands.
const TEXT_ONLY_FIELDS = ["awbNumber", "description"];

const FIELD_PRESETS = [
    { label: "AWB", field: { name: "awbNumber", title: "AWB Number", type: "text", mandatory: true } },
    { label: "Description", field: { name: "description", title: "Describe Issue", type: "textarea", mandatory: false } },
    { label: "Attachment", field: { name: "file", title: "Upload File", type: "file", mandatory: false } },
    { label: "Custom field", field: { name: "", title: "", type: "text", mandatory: false } },
];

const emptySub = { name: "", firstResponseHours: 4, resolutionHours: 48, defaultSeverity: "", allowedAfterDelivery: false, active: true, fields: [] };

/** The value that occurs most often (first seen wins a tie). */
const mostCommon = (values) => {
    const counts = new Map();
    values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
    let best = null, bestN = 0;
    counts.forEach((c, v) => { if (c > bestN) { best = v; bestN = c; } });
    return best;
};

/** Edit dialog for one subcategory — add when `original` is null. Names are fixed once created. */
const SubCategoryDialog = ({ open, category, original, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState(emptySub);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(original ? {
            name: original.name,
            firstResponseHours: toHours(original.firstResponseMinutes),
            resolutionHours: toHours(original.resolutionMinutes),
            defaultSeverity: original.defaultSeverity || "",
            allowedAfterDelivery: !!original.allowedAfterDelivery,
            active: original.active !== false,
            fields: (original.fields || []).map((f) => ({ ...f })),
        } : { ...emptySub, fields: [] });
    }, [open, original]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const setField = (i, key, value) => setForm((f) => ({ ...f, fields: f.fields.map((x, j) => (j === i ? { ...x, [key]: value } : x)) }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                name: form.name,
                firstResponseMinutes: toMinutes(form.firstResponseHours),
                resolutionMinutes: toMinutes(form.resolutionHours),
                defaultSeverity: form.defaultSeverity || null,
                allowedAfterDelivery: form.allowedAfterDelivery,
                active: form.active,
                fields: form.fields,
            };
            const url = original ? `${ADMIN}/${category.id}/subcategories?originalName=${encodeURIComponent(original.name)}`
                : `${ADMIN}/${category.id}/subcategories`;
            await putGeneric(url, body);
            enqueueSnackbar(original ? "Subcategory updated" : "Subcategory added", { variant: "success" });
            onSaved();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Could not save the subcategory"), { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: "14px", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" } } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
                <IconTile icon={AccountTreeOutlinedIcon} />
                <Box sx={{ minWidth: 0 }}>
                    <Eyebrow>{category?.categoryName}</Eyebrow>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>
                        {original ? `Edit ${original.name}` : `Add subcategory to ${category?.categoryName}`}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important", ...purpleControlsSx }}>
                <TextField label="Name" size="small" value={form.name} disabled={!!original}
                    helperText={original ? "Names can't be changed — tickets use them. Add a new one and switch this off instead." : ""}
                    onChange={(e) => set("name", e.target.value)} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                    <TextField label="First response (hours)" size="small" type="number" value={form.firstResponseHours}
                        onChange={(e) => set("firstResponseHours", e.target.value)} />
                    <TextField label="Resolution TAT (hours)" size="small" type="number" value={form.resolutionHours}
                        onChange={(e) => set("resolutionHours", e.target.value)} />
                    <TextField select label="Default severity" size="small" value={form.defaultSeverity}
                        SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                        onChange={(e) => set("defaultSeverity", e.target.value)}>
                        <MenuItem value="">Default (P2)</MenuItem>
                        {Object.keys(SEVERITY_META).map((s) => <MenuItem key={s} value={s}>{SEVERITY_META[s].label}</MenuItem>)}
                    </TextField>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    {[
                        ["allowedAfterDelivery", "Allowed on delivered orders", "Tickets can be raised after the order is delivered."],
                        ["active", "Offered for new tickets", "When off, Create Ticket no longer offers it."],
                    ].map(([key, label, help]) => (
                        <Box key={key} sx={{ border: `1px solid ${COLORS.grid}`, borderRadius: "10px", bgcolor: form[key] ? COLORS.purpleSoft : COLORS.ground,
                            px: 1.5, py: 1, transition: "background-color 150ms" }}>
                            <FormControlLabel sx={{ m: 0 }} control={<Switch checked={form[key]} onChange={(e) => set(key, e.target.checked)} />}
                                label={<Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{label}</Typography>} />
                            <Typography sx={{ fontSize: 12, color: MUTED, pl: 0.5 }}>{help}</Typography>
                        </Box>
                    ))}
                </Box>

                <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 0.25 }}>Form fields</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
                        What Create Ticket asks for this subcategory, in this order. Custom fields are saved on the ticket under their key.
                    </Typography>
                    {form.fields.length === 0 && (
                        <Typography sx={{ fontSize: 13, color: MUTED, border: `1px dashed ${COLORS.grid}`, borderRadius: "10px", px: 1.5, py: 1.25, mb: 1 }}>
                            No fields yet. Add one below.
                        </Typography>
                    )}
                    {form.fields.map((f, i) => (
                        <Box key={i} sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "1.1fr 1.6fr 0.9fr auto" }, gap: 1, alignItems: "center",
                            mb: 1, p: 1.25, borderRadius: "10px", bgcolor: COLORS.ground, border: `1px solid ${COLORS.grid}`, "& .MuiInputBase-root": { bgcolor: "#fff" } }}>
                            <TextField size="small" label="Key" value={f.name} onChange={(e) => setField(i, "name", e.target.value)} />
                            <TextField size="small" label="Label" value={f.title} onChange={(e) => setField(i, "title", e.target.value)} />
                            {/* AWBs aren't always digits (Ekart, Shadowfax), so AWB and description stay text. */}
                            <TextField select size="small" label="Type" value={f.type} onChange={(e) => setField(i, "type", e.target.value)}
                                disabled={TEXT_ONLY_FIELDS.includes(f.name)}
                                helperText={TEXT_ONLY_FIELDS.includes(f.name) ? "Always text" : undefined}>
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="textarea">Long text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="file">File</MenuItem>
                            </TextField>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                                <FormControlLabel sx={{ m: 0 }} label={<Typography sx={{ fontSize: 13 }}>Required</Typography>}
                                    control={<Checkbox size="small" checked={!!f.mandatory} onChange={(e) => setField(i, "mandatory", e.target.checked)} />} />
                                <IconButton size="small" aria-label="Remove field" sx={{ "&:hover": { color: ACCENTS.red.solid, bgcolor: ACCENTS.red.soft } }}
                                    onClick={() => setForm((x) => ({ ...x, fields: x.fields.filter((_, j) => j !== i) }))}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {FIELD_PRESETS.map((p) => (
                            <Button key={p.label} size="small" variant="outlined" startIcon={<AddIcon />}
                                sx={{ ...textBtnSx, borderRadius: "999px", borderColor: "#D6CBE6", "&:hover": { borderColor: COLORS.purple, bgcolor: COLORS.purpleSoft } }}
                                onClick={() => setForm((x) => ({ ...x, fields: [...x.fields, { ...p.field }] }))}>
                                {p.label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={cancelBtnSx}>Cancel</Button>
                <LoadingButton variant="contained" loading={saving} onClick={save} sx={gradientBtnSx}>Save</LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

/** One subcategory: name and fields on the left, its targets as chips, then its switch and Edit. */
const SubCategoryRow = ({ category, sub, onToggle, onEdit }) => {
    const off = sub.active === false;
    const sev = sub.defaultSeverity || "P2";
    const sevMeta = SEVERITY_META[sev];
    const firstReply = sub.firstResponseMinutes != null ? `${toHours(sub.firstResponseMinutes)}h` : "4h";
    const resolve = sub.resolutionMinutes != null ? `${toHours(sub.resolutionMinutes)}h` : "48h";
    const fields = sub.fields || [];
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2, rowGap: 1, px: 2, py: 1.5,
            borderTop: `1px solid ${COLORS.grid}`, transition: "background-color 150ms", "&:hover": { bgcolor: "#FBFAFD" } }}>
            <Box sx={{ flex: "1 1 260px", minWidth: 0, opacity: off ? 0.6 : 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, overflowWrap: "anywhere" }}>{sub.name}</Typography>
                    {off && <StatePill on={false} offLabel="Switched off" />}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                    <TextFieldsOutlinedIcon sx={{ fontSize: 15, color: MUTED }} aria-label="Form fields" />
                    {fields.length ? fields.map((f, i) => (
                        <Box key={`${f.name}-${i}`} component="span" sx={{ fontSize: 12, color: COLORS.ink2, px: 0.75, py: 0.125, borderRadius: "6px",
                            border: `1px solid ${COLORS.grid}`, bgcolor: "#fff", whiteSpace: "nowrap" }}>
                            {f.title || f.name}{f.mandatory ? <Box component="span" sx={{ color: ACCENTS.red.solid, fontWeight: 700 }} title="Required">*</Box> : ""}
                        </Box>
                    )) : <Typography component="span" sx={{ fontSize: 12, color: MUTED }}>No form fields</Typography>}
                </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", opacity: off ? 0.6 : 1 }}>
                <Pill accent={ACCENTS.blue} icon={TimerOutlinedIcon} title="Resolution TAT and first response target">
                    Resolve {resolve} · First reply {firstReply}
                </Pill>
                <Pill accent={sevMeta ? { soft: sevMeta.bg, solid: sevMeta.color } : OFF_GREY} icon={FlagOutlinedIcon} title="Default severity">
                    {sevMeta ? sevMeta.label : sev}
                </Pill>
                <Pill accent={sub.allowedAfterDelivery ? ACCENTS.green : OFF_GREY} icon={LocalShippingOutlinedIcon} title="Can tickets be raised on delivered orders?">
                    Delivered orders: {sub.allowedAfterDelivery ? "Allowed" : "Blocked"}
                </Pill>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                <Tooltip title={off ? "Switched off: not offered for new tickets" : "Offered for new tickets"}>
                    <Switch size="small" checked={!off} onChange={(e) => onToggle(category, sub, e.target.checked)}
                        inputProps={{ "aria-label": `${sub.name} offered for new tickets` }} />
                </Tooltip>
                <Tooltip title="Edit">
                    <IconButton size="small" aria-label={`Edit ${sub.name}`} onClick={() => onEdit(category, sub)}
                        sx={{ color: COLORS.ink3, "&:hover": { color: COLORS.purple, bgcolor: COLORS.purpleSoft } }}>
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

/**
 * Categories tab — the category / subcategory list behind Create Ticket and the filters. Proship
 * admins and Escalation admins can add categories and subcategories, set each subcategory's TAT,
 * default severity, delivered-order rule and form fields, and switch either off. Changes reach
 * every server within a minute (config cache TTL).
 */
const CategoriesPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [categories, setCategories] = useState(null);
    const [newCategory, setNewCategory] = useState("");
    const [adding, setAdding] = useState(false);
    const [dialog, setDialog] = useState({ open: false, category: null, original: null });
    const [query, setQuery] = useState("");
    const [show, setShow] = useState("all");
    const [collapsed, setCollapsed] = useState({});

    const load = useCallback(async () => {
        try {
            const res = await getGeneric(ADMIN);
            setCategories(res?.data || []);
        } catch (e) {
            setCategories([]);
            enqueueSnackbar(errMsg(e, "Could not load categories"), { variant: "error" });
        }
    }, [enqueueSnackbar]);

    useEffect(() => { load(); }, [load]);

    const changed = () => {
        invalidateCached("config/categories"); // Create Ticket and the filters re-read it
        load();
    };

    const addCategory = async () => {
        setAdding(true);
        try {
            await postGeneric(ADMIN, { categoryName: newCategory });
            enqueueSnackbar("Category added", { variant: "success" });
            setNewCategory("");
            changed();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Could not add the category"), { variant: "error" });
        } finally {
            setAdding(false);
        }
    };

    const toggleCategory = async (category, active) => {
        try {
            await patchGeneric(`${ADMIN}/${category.id}`, { active });
            changed();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Could not update the category"), { variant: "error" });
        }
    };

    const toggleSub = async (category, sub, active) => {
        try {
            await putGeneric(`${ADMIN}/${category.id}/subcategories?originalName=${encodeURIComponent(sub.name)}`, { ...sub, active });
            changed();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Could not update the subcategory"), { variant: "error" });
        }
    };

    const openEdit = (category, sub) => setDialog({ open: true, category, original: sub });

    // ── Summary numbers, from the list already loaded ──
    const stats = useMemo(() => {
        const cats = categories || [];
        const subs = cats.flatMap((c) => c.subCategories || []);
        return {
            cats: cats.length,
            activeCats: cats.filter((c) => c.active !== false).length,
            subs: subs.length,
            activeSubs: subs.filter((s) => s.active !== false).length,
            resolve: subs.length ? mostCommon(subs.map((s) => s.resolutionMinutes ?? DEFAULT_RESOLUTION_MIN)) : null,
            reply: subs.length ? mostCommon(subs.map((s) => s.firstResponseMinutes ?? DEFAULT_FIRST_RESPONSE_MIN)) : null,
        };
    }, [categories]);

    // ── Search and the Active / Switched off filter ──
    const q = query.trim().toLowerCase();
    const visible = useMemo(() => (categories || [])
        .filter((c) => (show === "all" ? true : show === "active" ? c.active !== false : c.active === false))
        .map((c) => {
            const subs = c.subCategories || [];
            if (!q || (c.categoryName || "").toLowerCase().includes(q)) return { category: c, subs, matchedSubs: false };
            const hits = subs.filter((s) => (s.name || "").toLowerCase().includes(q));
            return hits.length ? { category: c, subs: hits, matchedSubs: true } : null;
        })
        .filter(Boolean), [categories, show, q]);

    const allCollapsed = visible.length > 0 && visible.every((v) => collapsed[v.category.id]);
    const setAll = (value) => setCollapsed(Object.fromEntries(visible.map((v) => [v.category.id, value])));
    const isOpen = (id) => !!q || !collapsed[id];
    const collapseTimeout = reducedMotion() ? 0 : "auto";
    const loading = categories == null;

    return (
        <>
            <PageTitle>Escalation Management V2 — Categories</PageTitle>
            <EmsPageHeader icon={CategoryOutlinedIcon} title="Categories"
                sub="Ticket categories and subcategories — TAT, default severity and form fields." />
            <EscalationV2Nav />

            {/* Summary */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: { xs: 1.25, sm: 2 }, mb: 2 }}>
                <ConfigStat icon={CategoryOutlinedIcon} label="Categories" value={stats.cats} loading={loading} order={1}
                    caption={loading ? null : `${stats.activeCats} active`} />
                <ConfigStat icon={AccountTreeOutlinedIcon} label="Subcategories" value={stats.subs} accent="blue" loading={loading} order={2}
                    caption={loading ? null : `${stats.activeSubs} offered for new tickets`} />
                <ConfigStat icon={TimerOutlinedIcon} label="Usual resolution TAT" accent="green" loading={loading} order={3}
                    value={stats.resolve != null ? `${toHours(stats.resolve)}h` : "—"} caption="most common target" />
                <ConfigStat icon={ReplyOutlinedIcon} label="Usual first reply" accent="orange" loading={loading} order={4}
                    value={stats.reply != null ? `${toHours(stats.reply)}h` : "—"} caption="most common target" />
            </Box>

            {/* Toolbar: find, filter, add */}
            <Box sx={{ ...CARD_SX, mb: 2, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 1.5, ...purpleControlsSx, ...rise(5) }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField size="small" placeholder="Search categories or subcategories" value={query} onChange={(e) => setQuery(e.target.value)}
                        sx={{ flex: "1 1 260px", minWidth: 0, maxWidth: { sm: 380 } }}
                        inputProps={{ "aria-label": "Search categories or subcategories" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: MUTED }} /></InputAdornment>,
                            endAdornment: query ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" aria-label="Clear search" onClick={() => setQuery("")}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                                </InputAdornment>
                            ) : null,
                        }} />
                    <Segmented ariaLabel="Show categories" value={show} onChange={setShow} options={[
                        { value: "all", label: "All", count: stats.cats },
                        { value: "active", label: "Active", count: stats.activeCats, accent: "green" },
                        { value: "off", label: "Switched off", count: stats.cats - stats.activeCats, accent: "orange" },
                    ]} />
                    {visible.length > 1 && !q && (
                        <Button size="small" sx={{ ...textBtnSx, ml: { md: "auto" } }} startIcon={allCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
                            onClick={() => setAll(!allCollapsed)}>
                            {allCollapsed ? "Expand all" : "Collapse all"}
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", pt: 1.5, borderTop: `1px solid ${COLORS.grid}` }}>
                    <TextField size="small" label="New category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                        sx={{ flex: "1 1 220px", minWidth: 0, maxWidth: { sm: 320 } }} />
                    <LoadingButton variant="contained" loading={adding} disabled={!newCategory.trim()} onClick={addCategory}
                        startIcon={<AddIcon />} sx={gradientBtnSx}>
                        Add category
                    </LoadingButton>
                    <Typography sx={{ fontSize: 12, color: MUTED, flex: "1 1 240px" }}>
                        Names can&apos;t be changed after they&apos;re created — switch one off and add a new one instead.
                    </Typography>
                </Box>
            </Box>

            {/* List */}
            {loading ? (
                <Box sx={{ display: "grid", gap: 1.5 }}>
                    {[0, 1, 2].map((i) => (
                        <Box key={i} sx={{ ...CARD_SX, display: "flex", gap: 1.5, alignItems: "center" }}>
                            <Skeleton variant="rounded" width={34} height={34} />
                            <Box sx={{ flex: 1 }}><Skeleton width="30%" /><Skeleton width="18%" /></Box>
                        </Box>
                    ))}
                </Box>
            ) : categories.length === 0 ? (
                <ConfigEmpty icon={CategoryOutlinedIcon} title="No categories yet"
                    text="Add the first category above, then give it subcategories with their TAT, default severity and form fields." />
            ) : visible.length === 0 ? (
                <ConfigEmpty icon={SearchIcon} accent={ACCENTS.blue} title="Nothing matches"
                    text={q ? `No category or subcategory matches “${query.trim()}”${show !== "all" ? " in this view" : ""}.` : "No categories in this view."}
                    action={<Button sx={textBtnSx} onClick={() => { setQuery(""); setShow("all"); }}>Show all categories</Button>} />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {visible.map(({ category, subs, matchedSubs }, idx) => {
                        const off = category.active === false;
                        const all = category.subCategories || [];
                        const activeSubs = all.filter((s) => s.active !== false).length;
                        const open = isOpen(category.id);
                        return (
                            <Box key={category.id} sx={{ ...topBarCardSx(off ? { grad: "linear-gradient(90deg, #D1D5DB, #9CA3AF)" } : ACCENTS.purple),
                                ...rise(Math.min(idx, 3) + 6) }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", px: 2, pt: 2, pb: open ? 1.5 : 2 }}>
                                    <Box component="button" type="button" aria-expanded={open} aria-controls={`cat-${category.id}`}
                                        onClick={() => setCollapsed((c) => ({ ...c, [category.id]: !c[category.id] }))}
                                        sx={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 1.25, flex: "1 1 220px", minWidth: 0,
                                            borderRadius: "8px", "&:focus-visible": { outline: `2px solid ${COLORS.purple}`, outlineOffset: 2 } }}>
                                        <Box sx={{ opacity: off ? 0.55 : 1, display: "flex" }}>
                                            <IconTile icon={CategoryOutlinedIcon} accent={off ? OFF_GREY : ACCENTS.purple} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: off ? MUTED : NAVY, overflowWrap: "anywhere" }}>
                                                    {category.categoryName}
                                                </Typography>
                                                <StatePill on={!off} onLabel="Active" offLabel="Switched off" />
                                            </Box>
                                            <Typography sx={{ fontSize: 12, color: MUTED }}>
                                                {all.length} subcategor{all.length === 1 ? "y" : "ies"} · {activeSubs} offered for new tickets
                                                {matchedSubs ? ` · ${subs.length} matching` : ""}
                                            </Typography>
                                        </Box>
                                        <ExpandMoreIcon aria-hidden sx={{ ml: "auto", color: MUTED, transition: "transform 200ms", transform: open ? "rotate(180deg)" : "none",
                                            "@media (prefers-reduced-motion: reduce)": { transition: "none" } }} />
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ...purpleControlsSx }}>
                                        <FormControlLabel sx={{ m: 0 }} label={<Typography sx={{ fontSize: 12, fontWeight: 600 }}>Active</Typography>}
                                            control={<Switch size="small" checked={!off} inputProps={{ "aria-label": `${category.categoryName} active` }}
                                                onChange={(e) => toggleCategory(category, e.target.checked)} />} />
                                        <Button size="small" startIcon={<AddIcon />} sx={textBtnSx}
                                            onClick={() => setDialog({ open: true, category, original: null })}>
                                            Add subcategory
                                        </Button>
                                    </Box>
                                </Box>
                                <Collapse in={open} timeout={collapseTimeout} id={`cat-${category.id}`}>
                                    <Box sx={purpleControlsSx}>
                                        {subs.length === 0 ? (
                                            <Box sx={{ borderTop: `1px solid ${COLORS.grid}`, px: 2, py: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                <Typography sx={{ fontSize: 13, color: MUTED }}>No subcategories yet.</Typography>
                                                <Button size="small" startIcon={<AddIcon />} sx={textBtnSx}
                                                    onClick={() => setDialog({ open: true, category, original: null })}>
                                                    Add the first one
                                                </Button>
                                            </Box>
                                        ) : subs.map((sub) => (
                                            <SubCategoryRow key={sub.name} category={category} sub={sub} onToggle={toggleSub} onEdit={openEdit} />
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <SubCategoryDialog
                open={dialog.open}
                category={dialog.category}
                original={dialog.original}
                onClose={() => setDialog({ open: false, category: null, original: null })}
                onSaved={() => { setDialog({ open: false, category: null, original: null }); changed(); }}
            />
        </>
    );
};

export default CategoriesPage;
